import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TenantResponse } from '../interfaces/tenant-response.interface';

type TenantBranding = TenantResponse['branding'];
/** Patch parcial de branding — cada campo aceita `null`/ausente, que é ignorado (mantém o valor atual). */
type BrandingUpdate = { [K in keyof TenantBranding]?: TenantBranding[K] | null };

/**
 * Slug fixo para testar white-label em ambientes não-produção.
 * Altere para uma string não-vazia para simular um white-label (ex: 'goias').
 * Em produção este valor é ignorado; o slug é extraído do hostname.
 */
// exemplo: const DEV_WHITE_LABEL_SLUG = 'goias';
const DEV_WHITE_LABEL_SLUG = '';

@Injectable({ providedIn: 'root' })
export class TenantService {

  private tenantSubject = new BehaviorSubject<TenantResponse | null>(null);

  /** Emite o tenant atual ou null quando não é white-label. */
  readonly tenant$: Observable<TenantResponse | null> = this.tenantSubject.asObservable();

  constructor(
    private http: HttpClient,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /** Retorna o tenant carregado ou null. */
  get tenant(): TenantResponse | null {
    return this.tenantSubject.value;
  }

  /** Indica se a aplicação está rodando no contexto de um white-label. */
  get isWhiteLabel(): boolean {
    return this.tenantSubject.value !== null;
  }

  /** Indica se a visualização atual deve ser a de uma entidade nível cidade (simulado). */
  get isCityView(): boolean {
    return this.tenant?.level === 'CITY';
  }

  /** Id da cidade a exibir quando `isCityView` for verdadeiro. */
  get cityViewId(): string {
    return this.tenant?.levelId as string;
  }

  /**
   * Inicializa o contexto white-label.
   * Chamado via APP_INITIALIZER antes da primeira renderização.
   */
  initialize(): Promise<void> {
    const slug = this.resolveSlug();

    if (!slug) {
      return Promise.resolve();
    }

    return this.loadTenant(slug).toPromise();
  }

  // ---------------------------------------------------------------------------
  // Privados
  // ---------------------------------------------------------------------------

  /**
   * Resolve o slug do white-label a partir do contexto atual.
   *
   * - Produção: www.<slug>.brightcities.city → slug; www.brightcities.city → app base
   * - Homologação: <slug>.gsctool-web-dev.actionlabs.com.br → slug; sem subdomínio → app base
   * - Localhost: usa DEV_WHITE_LABEL_SLUG como fallback fixo.
   */
  resolveSlug(): string | null {
    const hostname = window.location.hostname;

    // Produção: www.<slug>.brightcities.city
    const prodBase = 'brightcities.city';
    if (hostname === `www.${prodBase}`) return null;
    if (hostname.endsWith(`.${prodBase}`)) {
      const slug = hostname.slice(0, hostname.length - prodBase.length - 1).replace(/^www\./, '');
      return slug || null;
    }

    // Homologação: <slug>.gsctool-web-dev.actionlabs.com.br
    const homologBase = 'gsctool-web-dev.actionlabs.com.br';
    if (hostname === homologBase) return null;
    if (hostname.endsWith(`.${homologBase}`)) {
      const slug = hostname.slice(0, hostname.length - homologBase.length - 1);
      return slug || null;
    }

    // Fallback local (localhost / variável fixa)
    return DEV_WHITE_LABEL_SLUG || null;
  }

  /**
   * Carrega o tenant por slug SEM os redirects do bootstrap (404 → deployUrl etc.).
   * Uso: página de print renderizada por origem interna, onde o hostname não resolve
   * tenant — o slug vem no query param `tenant` enviado pelo job. Falha é silenciosa:
   * a geração segue com os fallbacks de branding (logo do template/Bright Cities).
   */
  loadTenantBySlug(slug: string): Observable<void> {
    return this.http.get<TenantResponse>(`${environment.apiURL}/frontEndOpen/tenant/resolve/${slug}`).pipe(
      tap(tenant => {
        this.tenantSubject.next(tenant);
        this.applyBranding(tenant.branding);
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  /**
   * Carrega os dados do tenant via API.
  */
  private loadTenant(slug: string): Observable<void> {
    
    return this.http.get<TenantResponse>(`${environment.apiURL}/frontEndOpen/tenant/resolve/${slug}`).pipe(
      tap(tenant => {
        this.tenantSubject.next(tenant);
        this.applyBranding(tenant.branding);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          window.location.href = environment.deployUrl;
        } else if (err.status === 401) {
          window.location.href = `${window.location.origin}/login`;
        }
        return of(null);
      }),
      map(() => undefined)
    );
  }

  /**
   * Aplica a identidade visual no documento (CSS Custom Properties no :root, título
   * da aba, favicon). Público para poder ser reaplicado após o PUT de Aparência do
   * sistema salvar, refletindo a mudança sem precisar recarregar a página. Campos
   * `null`/ausentes são ignorados — mantém o que já estava aplicado.
   */
  applyBranding(branding: BrandingUpdate): void {
    const root = this.document.documentElement;
    const cssVars: Record<string, string | null | undefined> = {
      '--wl-primary': branding.primaryColor,
      '--wl-secondary': branding.secondaryColor,
      '--wl-accent': branding.accentColor,
      '--wl-background': branding.backgroundColor,
      '--wl-primary-text': branding.primaryTextColor,
      '--wl-secondary-text': branding.secondaryTextColor,
      '--wl-warning-color': branding.warningColor,
    };
    Object.entries(cssVars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
    });

    if (branding.pageTitle) {
      this.titleService.setTitle(branding.pageTitle);
    }

    if (branding.faviconUrl) {
      const link: HTMLLinkElement =
        this.document.querySelector("link[rel*='icon']") || this.document.createElement('link');
      link.rel = 'icon';
      link.href = branding.faviconUrl;
      this.document.head.appendChild(link);
    }
  }

  /**
   * Atualiza o branding do tenant em memória (para quem lê `tenant`/`tenant$`, ex.:
   * logo/rodapé do site) e reaplica a identidade visual no documento. Chamar após o
   * PUT de Aparência do sistema salvar com sucesso.
   */
  updateBranding(branding: BrandingUpdate): void {
    const current = this.tenantSubject.value;
    if (current) {
      this.tenantSubject.next({
        ...current,
        branding: {
          accentColor: branding.accentColor ?? current.branding.accentColor,
          backgroundColor: branding.backgroundColor ?? current.branding.backgroundColor,
          bannerUrl: branding.bannerUrl !== undefined ? branding.bannerUrl : current.branding.bannerUrl,
          faviconUrl: branding.faviconUrl ?? current.branding.faviconUrl,
          logoUrl: branding.logoUrl !== undefined ? branding.logoUrl : current.branding.logoUrl,
          pageTitle: branding.pageTitle ?? current.branding.pageTitle,
          primaryColor: branding.primaryColor ?? current.branding.primaryColor,
          primaryTextColor: branding.primaryTextColor ?? current.branding.primaryTextColor,
          secondaryColor: branding.secondaryColor ?? current.branding.secondaryColor,
          secondaryTextColor: branding.secondaryTextColor ?? current.branding.secondaryTextColor,
          warningColor: branding.warningColor ?? current.branding.warningColor,
        },
      });
    }
    this.applyBranding(branding);
  }
}
